import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import {
  CaretDown,
  CaretRight,
  Warning,
  Lightbulb,
  ArrowClockwise,
  ClipboardText,
  CheckSquare,
} from "@phosphor-icons/react";

import { ObjectiveAnalytics, ProgressDetails } from "../../../../graphql/generated/graphql";
import { formatTime } from "../../../../utils/analyticsUtils";
import CustomText from "../../../common/general/CustomText/CustomText";
import StatBadge from "./StatBadge";

interface ObjectivePanelProps {
  objective: ObjectiveAnalytics;
}

const ObjectivePanel: React.FC<ObjectivePanelProps> = ({ objective }) => {
  const [expanded, setExpanded] = useState(false);
  const [progressDetails, setProgressDetails] = useState<ProgressDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(false);

  const handleExpand = async () => {
    if (!expanded && !progressDetails) {
      setLoading(true);
      // Use ONLY the real progress_details from the objective data - NO MOCK DATA
      const realProgressDetails = objective.progress_details;
      setTimeout(() => {
        setProgressDetails(realProgressDetails);
        setLoading(false);
        setExpanded(true);
      }, 100);
    } else {
      setExpanded(!expanded);
    }
  };
  const truncateText = (text: string, maxLength: number) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };
  const MAX_SUMMARY_LENGTH = 200;

  const hasDetailedData = objective.success_points > 0 || objective.confusion_points > 0;

  return (
    <View style={[styles.objectiveItem, { opacity: hasDetailedData ? 1 : 0.6 }]}>
      <TouchableOpacity style={styles.objectiveHeader} onPress={handleExpand} disabled={!hasDetailedData}>
        <View style={styles.objectiveHeaderContent}>
          {expanded ? (
            <CaretDown size={16} color="#6B7280" style={styles.objectiveExpandIcon} />
          ) : (
            <CaretRight size={16} color="#6B7280" style={styles.objectiveExpandIcon} />
          )}
          <CustomText
            text={objective.label}
            size="s"
            weight="bold"
            style={styles.objectiveLabel}
            useTranslationText={false}
          />
        </View>
      </TouchableOpacity>

      <View style={styles.objectiveMetrics}>
        <StatBadge label="Success" value={objective.success_points || 0} color="#10B981" />
        <StatBadge label="Confusion" value={objective.confusion_points || 0} color="#EF4444" />
        <StatBadge label="Questions" value={objective.progress_details?.questions_asked || 0} color="#3B82F6" />
        <StatBadge
          label="Student Msgs"
          value={objective.progress_details?.student_messages_count || 0}
          color="#6366F1"
        />
        <StatBadge label="AI Msgs" value={objective.progress_details?.ai_messages_count || 0} color="#8B5CF6" />
        <StatBadge label="Time" value={formatTime(objective.average_time_taken || 0)} color="#6B7280" />
      </View>

      {expanded && (
        <View style={styles.objectiveExpandedContent}>
          {loading ? (
            <View style={styles.objectiveLoadingContainer}>
              <CustomText text="Loading progress details..." size="s" />
            </View>
          ) : progressDetails ? (
            <View style={styles.progressDetailsContainer}>
              {/* Summary */}
              {progressDetails.summary && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <ClipboardText size={16} color="#6B7280" />
                    <CustomText text="Summary" size="s" weight="bold" style={styles.progressDetailTitle} />
                  </View>
                  <View style={styles.progressDetailContent}>
                    <CustomText
                      text={
                        showFullSummary
                          ? progressDetails.summary
                          : truncateText(progressDetails.summary, MAX_SUMMARY_LENGTH)
                      }
                      size="xs"
                      useTranslationText={false}
                    />
                    {progressDetails.summary.length > MAX_SUMMARY_LENGTH && (
                      <TouchableOpacity onPress={() => setShowFullSummary(!showFullSummary)}>
                        <CustomText
                          text={showFullSummary ? "Show Less" : "Show More"}
                          size="xs"
                          style={{ color: "#3B82F6" }}
                        />
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              )}

              {/* Level & Analytics */}
              <View style={styles.progressStatsGrid}>
                {progressDetails.level && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Level" size="xs" style={styles.progressStatLabel} />
                    <CustomText text={progressDetails.level} size="s" weight="bold" useTranslationText={false} />
                  </View>
                )}
                {progressDetails.sentiment_analysis && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Sentiment" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={progressDetails.sentiment_analysis}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={{ textTransform: "capitalize" }}
                    />
                  </View>
                )}
                {progressDetails.originality_analysis && progressDetails.originality_analysis !== "0" && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Originality" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={progressDetails.originality_analysis}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.average_student_question_complexity && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Question Complexity" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={progressDetails.average_student_question_complexity}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
              </View>

              {/* Message Counts */}
              <View style={styles.progressStatsGrid}>
                {progressDetails.questions_asked !== undefined && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Questions Asked" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={progressDetails.questions_asked}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.student_messages_count !== undefined && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Student Msgs" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={progressDetails.student_messages_count}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.ai_messages_count !== undefined && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="AI Msgs" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={progressDetails.ai_messages_count}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
              </View>

              {/* Response Times */}
              <View style={styles.progressStatsGrid}>
                {progressDetails.average_student_response_time !== undefined && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Avg Student Response" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={`${Math.round(progressDetails.average_student_response_time)}s`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.average_ai_response_time !== undefined && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Avg AI Response" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={`${Math.round(progressDetails.average_ai_response_time)}s`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.total_time_taken !== undefined && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Total Time Taken" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={formatTime(progressDetails.total_time_taken)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
              </View>

              {/* Success Points Details */}
              {progressDetails.success_points && progressDetails.success_points.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <CheckSquare size={20} color="#10B981" />
                    <CustomText text="Success Points" size="s" weight="bold" style={styles.successProgressTitle} />
                  </View>
                  <View style={styles.progressDetailsList}>
                    {progressDetails.success_points.map((point, i) => (
                      <View key={i} style={styles.successProgressItem}>
                        <CustomText text={point} size="xs" useTranslationText={false} />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Confusion Points Details */}
              {progressDetails.confusion_points && progressDetails.confusion_points.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <Warning size={20} color="#DC2626" />
                    <CustomText text="Confusion Points" size="s" weight="bold" style={styles.confusionProgressTitle} />
                  </View>
                  <View style={styles.progressDetailsList}>
                    {progressDetails.confusion_points.map((point, i) => (
                      <View key={i} style={styles.confusionProgressItem}>
                        <CustomText text={point} size="xs" useTranslationText={false} />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Recommendations */}
              {progressDetails.further_recommendations && progressDetails.further_recommendations.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <Lightbulb size={20} color="#059669" />
                    <CustomText
                      text="Recommendations"
                      size="s"
                      weight="bold"
                      style={styles.recommendationProgressTitle}
                    />
                  </View>
                  <View style={styles.progressRecommendationsGrid}>
                    {progressDetails.further_recommendations.map((rec, i) => (
                      <View key={i} style={styles.progressRecommendationChip}>
                        <CustomText
                          text={rec}
                          size="xs"
                          style={styles.progressRecommendationText}
                          useTranslationText={false}
                        />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Distractions */}
              {progressDetails.distractions && progressDetails.distractions.length > 0 && (
                <View style={styles.progressDetailSection}>
                  <View style={styles.progressDetailHeader}>
                    <ArrowClockwise size={20} color="#F59E0B" />
                    <CustomText text="Distractions" size="s" weight="bold" style={styles.distractionProgressTitle} />
                  </View>
                  <View style={styles.progressDetailsList}>
                    {progressDetails.distractions.map((distraction, i) => (
                      <View key={i} style={styles.distractionProgressItem}>
                        <CustomText text={distraction} size="xs" useTranslationText={false} />
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Message & Time Statistics */}
              <View style={[styles.progressStatsGrid, { marginBottom: 0 }]}>
                {progressDetails.num_messages && progressDetails.num_messages > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Total Messages" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={`${progressDetails.num_messages}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.student_messages_count && progressDetails.student_messages_count > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Student Messages" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={`${progressDetails.student_messages_count}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.ai_messages_count && progressDetails.ai_messages_count > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="AI Messages" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={`${progressDetails.ai_messages_count}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.questions_asked !== undefined && progressDetails.questions_asked > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Questions Asked" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={`${progressDetails.questions_asked}`}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.average_student_response_time && progressDetails.average_student_response_time > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Avg Student Response" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={formatTime(progressDetails.average_student_response_time)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.average_ai_response_time && progressDetails.average_ai_response_time > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Avg AI Response" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={formatTime(progressDetails.average_ai_response_time)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
                {progressDetails.total_time_taken && progressDetails.total_time_taken > 0 && (
                  <View style={styles.progressStatItem}>
                    <CustomText text="Total Time" size="xs" style={styles.progressStatLabel} />
                    <CustomText
                      text={formatTime(progressDetails.total_time_taken)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                )}
              </View>
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  objectiveItem: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 12,
  },
  objectiveHeader: {
    marginBottom: 8,
  },
  objectiveLabel: {
    color: "#374151",
  },
  objectiveMetrics: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  objectiveHeaderContent: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  objectiveExpandIcon: {
    marginRight: 8,
  },
  objectiveExpandedContent: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  objectiveLoadingContainer: {
    padding: 16,
    alignItems: "center",
  },
  progressDetailsContainer: {
    backgroundColor: "#FAFAFA",
    borderRadius: 8,
    padding: 12,
  },
  progressDetailSection: {
    marginBottom: 16,
  },
  progressDetailHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  progressDetailTitle: {
    marginLeft: 8,
    color: "#3B82F6",
  },
  progressDetailContent: {
    backgroundColor: "#FFFFFF",
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  progressStatsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },
  progressStatItem: {
    minWidth: 100,
    maxWidth: "100%",
    padding: 8,
    alignItems: "center",
    gap: 4,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  progressStatLabel: {
    color: "#6B7280",
  },
  successProgressTitle: {
    marginLeft: 8,
    color: "#059669",
  },
  progressDetailsList: {
    gap: 4,
  },
  successProgressItem: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0FDF4",
    borderLeftWidth: 4,
    borderLeftColor: "#10B981",
    padding: 8,
    borderRadius: 4,
  },
  confusionProgressTitle: {
    marginLeft: 8,
    color: "#DC2626",
  },
  confusionProgressItem: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FEF2F2",
    borderLeftWidth: 4,
    borderLeftColor: "#EF4444",
    padding: 8,
    borderRadius: 4,
  },
  recommendationProgressTitle: {
    marginLeft: 8,
    color: "#059669",
  },
  progressRecommendationsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  progressRecommendationChip: {
    backgroundColor: "#F0FDF4",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#10B981",
  },
  progressRecommendationText: {
    color: "#059669",
    fontWeight: "500",
  },
  distractionProgressTitle: {
    marginLeft: 8,
    color: "#F59E0B",
  },
  distractionProgressItem: {
    backgroundColor: "#FFFBEB",
    borderLeftWidth: 4,
    borderLeftColor: "#F59E0B",
    padding: 8,
    borderRadius: 4,
  },
});

export default ObjectivePanel;
